import { styled } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { ACTIVE_CASE_STATUSES, caseStatusMap } from '#entities';
import {
  sectionClasses,
  useSelectVisibleWidgetInstancesByType,
  ActiveCaseCards,
  type ActiveCaseCardsProps,
  type WidgetConfigBase,
  type WidgetProps,
} from '#features';

import { Widget } from '../base';

const Root = styled(Widget)(() => ({
  flexGrow: 1,
  maxHeight: '100%',
  overflow: 'visible',
}));

type Settings = Omit<
  ActiveCaseCardsProps,
  'maxHeight' | 'availableHeight' | 'instance' | 'instanceOptions'
>;

const config: WidgetConfigBase<Settings> = {
  caseStatuses: {
    type: 'select',
    label: 'Case Statuses',
    options: ACTIVE_CASE_STATUSES.map(status => ({
      label: caseStatusMap[status] ?? status,
      value: status,
    })),
    multiple: true,
    defaultValue: [],
  },
  showReturningToBase: {
    type: 'boolean',
    label: 'Show Returning to Base',
    defaultValue: false,
  },
  shareSettings: {
    type: 'boolean',
    label: 'Share Settings with Other Instances',
    hidden: true,
    defaultValue: false,
  },
  sortBy: {
    type: 'select',
    label: 'Sort By',
    options: [
      { label: 'ID', value: 'id' },
      { label: 'Donor Type', value: 'donorType' },
      { label: 'Label', value: 'label' },
      { label: 'OSC Status', value: 'oscStatus' },
      { label: 'OSC Status Sequence', value: 'oscStatusSequence' },
      { label: 'Donor/Time String', value: 'donorORTimeString' },
    ],
    defaultValue: 'oscStatusSequence',
  },
  maxRows: {
    type: 'number',
    label: 'Max Rows',
    defaultValue: 0,
    min: 0,
    step: 1,
  },
  highlightDurationSeconds: {
    type: 'number',
    label: 'Highlight Fade Out Duration (seconds)',
    defaultValue: 5,
    min: 0,
    step: 1,
  },
  variant: {
    type: 'select',
    label: 'Variant',
    hidden: true,
    options: [
      { label: 'Horizontal', value: 'horizontal' },
      { label: 'Vertical', value: 'vertical' },
    ],
    defaultValue: 'horizontal',
  },
};

export const WidgetActiveCases = (props: WidgetProps<Settings>) => {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  const { showSettingsButton = true, className, instance } = props;

  const visibleInstances =
    useSelectVisibleWidgetInstancesByType('active-cases');

  const instanceOptions = {
    totalInstances: visibleInstances.length,
    instanceIndex: visibleInstances.findIndex(wi => wi.id === instance?.id),
  };

  useEffect(() => {
    const element = ref.current;
    const parent = element?.closest(`.${sectionClasses.body}`);
    if (!element || !parent) {
      return;
    }

    const ro = new ResizeObserver(() => {
      setMaxHeight(
        element.clientHeight - parent.scrollHeight + parent.clientHeight,
      );
    });
    ro.observe(element);
    return () => ro.disconnect();
  }, []);

  return (
    <Root
      ref={ref}
      config={config}
      instance={instance}
      showSettingsButton={showSettingsButton}
      className={className}
    >
      {settings => (
        <ActiveCaseCards
          {...settings}
          instance={instance}
          instanceOptions={instanceOptions}
          maxHeight={maxHeight}
        />
      )}
    </Root>
  );
};

WidgetActiveCases.config = config;
